import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Text,
  VisuallyHidden,
  Placeholder,
} from "@telegram-apps/telegram-ui";
import { openInvoice } from "@telegram-apps/sdk";
import ApiService from "../../handlers/apiService";
import { RechargeIntervalTypeEnum } from "../../handlers/types";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { useGameStore, selectUser } from "../../store/gameStore";
import { TUIModalHeader } from "../TUIModalHeader/TUIModalHeader";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import "./RechargeModal.css";

interface RechargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRechargeSuccess?: () => void;
}

export function RechargeModal({
  isOpen,
  onClose,
  onRechargeSuccess,
}: RechargeModalProps): JSX.Element {
  const { t } = useI18n();
  const updateMana = useGameStore.getState().updateMana;
  const user = useGameStore(selectUser);
  const [rechargeOptions, setRechargeOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      fetchRechargeOptions();
      refreshUserData();
    }
  }, [isOpen]);

  const refreshUserData = async () => {
    // User data is now automatically available from the store
    setUserData(user);
  };

  const fetchRechargeOptions = async () => {
    try {
      setIsDataLoading(true);
      setError(null);
      const response =
        await ApiService.getInstance().api.manaRechargeOptionsList();
      setRechargeOptions(response.options || []);
    } catch (error) {
      console.error("Failed to fetch recharge options:", error);
      setError(t("common.error"));
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleRecharge = async (rechargeType: RechargeIntervalTypeEnum) => {
    try {
      setIsLoading(true);
      setError(null);

      // Call the mana recharge API and get updated mana data
      const updatedManaData =
        await ApiService.getInstance().api.manaRechargeCreate({
          rechargeType,
        });

      // Update the user's mana data in store
      if (updatedManaData) {
        updateMana(updatedManaData);

        // Fetch fresh user data to get updated balance
        await useGameStore.getState().refreshUserData();
      }

      onRechargeSuccess?.();
      onClose();
    } catch (error: any) {
      console.error("Failed to recharge mana:", error);

      // Check if error is due to insufficient balance
      if (
        error?.response?.status === 400 ||
        error?.message?.includes("balance")
      ) {
        setError(t("mana.insufficient_balance") || "Insufficient balance");
      } else {
        setError(t("mana.recharge_failed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getSingleRechargeOption = () => {
    return rechargeOptions.find(
      (option) => option.type === RechargeIntervalTypeEnum.Single
    );
  };

  const getAutoRechargeOptions = () => {
    return rechargeOptions.filter(
      (option) =>
        option.type !== RechargeIntervalTypeEnum.Single &&
        option.type !== RechargeIntervalTypeEnum.Value5Days
    );
  };

  const canAfford = (price: number): boolean => {
    return userData ? userData.balance >= price : false;
  };

  const singleRechargeOption = getSingleRechargeOption();
  const autoRechargeOptions = getAutoRechargeOptions();

  return (
    <Modal
      open={isOpen}
      header={<TUIModalHeader />}
      onOpenChange={(open) => !open && onClose()}
      className="recharge-modal"
      aria-labelledby="recharge-modal-title"
    >
      <VisuallyHidden>
        <DialogDescription>{t("mana.recharge_description")}</DialogDescription>
        <DialogTitle>{t("mana.recharge_title")}</DialogTitle>
      </VisuallyHidden>

      <div className="recharge-modal__content">
        {/* Title */}
        <Text id="recharge-modal-title" className="recharge-modal__title">
          {t("mana.recharge_title")}
        </Text>

        {isDataLoading ? (
          <Placeholder
            header={t("common.loading")}
            description={t("mana.loading_options")}
          />
        ) : error ? (
          <Placeholder
            header={t("common.error")}
            description={error}
            action={
              <Button onClick={fetchRechargeOptions}>
                {t("common.tryAgain")}
              </Button>
            }
          />
        ) : (
          <>
            {/* Description - Always show since modal only opens when paused */}
            <Text className="recharge-modal__description">
              {t("mana.recharge_description")}
            </Text>

            {/* All Recharge Options in One List */}
            <div className="recharge-modal__auto-options">
              {/* Single Recharge Option (5 hours) - First in List */}
              {singleRechargeOption && (
                <div className="recharge-modal__auto-option">
                  <Text className="recharge-modal__auto-duration">
                    {t("mana.duration_5_hours")}
                  </Text>
                  <Button
                    onClick={() =>
                      handleRecharge(RechargeIntervalTypeEnum.Single)
                    }
                    size="s"
                    className={`recharge-modal__auto-button ${
                      !canAfford(singleRechargeOption.totalPrice)
                        ? "recharge-modal__button--disabled"
                        : ""
                    }`}
                    disabled={
                      isLoading || !canAfford(singleRechargeOption.totalPrice)
                    }
                  >
                    <span className="recharge-modal__button-text">
                      {t("mana.activate_for_amount", {
                        amount: singleRechargeOption.totalPrice,
                      })}
                    </span>
                    <span className="recharge-modal__coin-icon" />
                  </Button>
                </div>
              )}

              {/* Auto Recharge Options (excluding 5 days) */}
              {autoRechargeOptions.map((option) => (
                <div key={option.type} className="recharge-modal__auto-option">
                  <Text className="recharge-modal__auto-duration">
                    {option.type === RechargeIntervalTypeEnum.Day
                      ? t("mana.duration_1_day")
                      : option.type === RechargeIntervalTypeEnum.Value3Days
                      ? t("mana.duration_3_days")
                      : `${Math.floor(
                          option.duration / (1000 * 60 * 60 * 24)
                        )} ${t("mana.days")}`}
                  </Text>
                  <Button
                    onClick={() => handleRecharge(option.type)}
                    size="s"
                    className={`recharge-modal__auto-button ${
                      !canAfford(option.totalPrice)
                        ? "recharge-modal__button--disabled"
                        : ""
                    }`}
                    disabled={isLoading || !canAfford(option.totalPrice)}
                  >
                    <span className="recharge-modal__button-text">
                      {t("mana.activate_for_amount", {
                        amount: option.totalPrice,
                      })}
                    </span>
                    <span className="recharge-modal__coin-icon" />
                  </Button>
                </div>
              ))}
            </div>

            {error && (
              <Text className="recharge-modal__error" color="danger">
                {error}
              </Text>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
